// Safety invariants for the ping/pong fixture. Each returns at most one
// violation object { invariant, message }; the engine appends new ones to
// state.violations after every transition.
import { MSG_PING, MSG_PONG } from './model';

function pingMessages(state) {
  return state.events.filter(
    (e) => e.kind === 'message' && e.payload && e.payload.msgType === MSG_PING
  );
}

function pongMessages(state) {
  return state.events.filter(
    (e) => e.kind === 'message' && e.payload && e.payload.msgType === MSG_PONG
  );
}

// Every executed ping id must be unique: a retry must hit the cache, never
// re-execute.
export function atMostOnceExecution(state) {
  const pongerId = state.config.ponger;
  const node = state.nodes[pongerId];
  if (!node) return null;
  const seen = new Set();
  for (const id of node.durable.executedPingIds) {
    if (seen.has(id)) {
      return {
        invariant: 'at-most-once-execution',
        message: 'ping ' + id + ' executed more than once',
      };
    }
    seen.add(id);
  }
  return null;
}

// Every pong must answer a ping the pinger actually sent.
export function pongAuthenticity(state) {
  const pingerId = state.config.pinger;
  const node = state.nodes[pingerId];
  if (!node) return null;
  const sent = new Set(node.durable.sentPingIds);
  const bad = pongMessages(state).find((e) => !sent.has(e.payload.pingId));
  if (bad) {
    return {
      invariant: 'pong-authenticity',
      message: 'pong ' + bad.payload.pingId + ' answers an unknown ping',
    };
  }
  const stale = node.durable.acceptedPongs.find((id) => !sent.has(id));
  if (stale) {
    return {
      invariant: 'pong-authenticity',
      message: 'accepted pong ' + stale + ' was never sent',
    };
  }
  return null;
}

// Retries must reuse the outstanding ping identity: the number of distinct
// ping ids in flight can never exceed the number of accepted commands.
export function retryPreservesIdentity(state) {
  const pingerId = state.config.pinger;
  const node = state.nodes[pingerId];
  if (!node) return null;
  const distinct = new Set(pingMessages(state).map((e) => e.payload.pingId));
  if (distinct.size > node.durable.commandsAccepted) {
    return {
      invariant: 'retry-preserves-identity',
      message:
        distinct.size +
        ' distinct ping ids for ' +
        node.durable.commandsAccepted +
        ' accepted commands',
    };
  }
  return null;
}

export function checkPingPongInvariants(state) {
  return [atMostOnceExecution, pongAuthenticity, retryPreservesIdentity]
    .map((fn) => fn(state))
    .filter(Boolean);
}
